import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import {
  fnGetBfWhQtOrLotInv,
  fnGetBfWhSnInv,
  fnGetBarcodeScanBfInv,
} from "@shared/utils/InvUtils";
import {
  getInitFormData,
  applyAutoSlip,
  fnGetIotype,
  roundNum,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_MTL_MOW_REG_010_POP_001 from "@menu/MTL/SN_MTL_MOW_REG_010/SN_MTL_MOW_REG_010_POP_001";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";
import SnBarcodeBox from "@shared/components/SnBarcodeBox";
import SnSwitch, { SnSwitchHandle } from "@shared/components/SnSwitch";
import SnNumberBox from "@shared/components/SnNumberBox";
import SnLabel from "@shared/components/SnLabel";
import * as converter from "@shared/utils/ValueConverterUtils";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_MOW_REG_010 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
    grid4: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const txtBarcodeRef = useRef<any>(null);
  const txtBarcodeInputRef = useRef<any>(null);
  const numberBarcodeQtRef = useRef<any>(null);
  const txtDirectBarcodeRef = useRef<any>(null);
  const txtDirectBarcodeInputRef = useRef<any>(null);
  const numberDirectBarcodeQtRef = useRef<any>(null);
  let isDirectScan = useRef<any>(false);

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  const iotypeRef = useRef<any[]>([]);
  //#endregion

  // 재고리스트
  const [lotSnData, setLotSnData] = useState<Record<string, any>[]>([]);
  const lotSnDataRef = useRef<Record<string, any>[]>([]);

  // 스위치
  const switchRef = useRef<SnSwitchHandle>(null);

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
    grid4: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid4: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
          grid4: rtn.grid.grid4,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "2", "5", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  useEffect(() => {
    lotSnDataRef.current = lotSnData;
  }, [lotSnData]);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_mow_reg_010_01",
        params: searchDataToUse,
      };

      setLotSnData([]);

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: [],
        grid4: data[2] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      const addedNode = getGridCurrent("grid1").addRow();
      addedNode?.setDataValue("cd_io", addedNode.data?.cd_io);
    } catch (error: any) {}
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeH) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    // 삭제할 헤더의 no_io로 출고정보를 필터한다.
    getGridCurrent("grid4").applyFilters({
      no_io: focusedRowNodeH.data.no_io,
    });

    getGridCurrent("grid4").deleteAllRows();
    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL.data.state === "insert") {
      resetRowData();

      // 신규 등록중인 불러온 재고정보 지움
      setLotSnData((prev) =>
        prev.filter(
          (item) =>
            !(
              item.no_io === focusedRowNodeL.data.no_src &&
              item.no_line_io === focusedRowNodeL.data.no_line_src
            )
        )
      );
    } else {
      getGridCurrent("grid4").deleteAllRows();
    }

    getGridCurrent("grid2").deleteSelectedRows();

    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      getGridCurrent("grid3").applyFilters({
        no_io: "",
        no_line_io: 0,
      });
    }
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const qtiohChanges = getGridCurrent("grid1").getChange();
      const qtioChanges = getGridCurrent("grid2").getChange();
      const qtiolotsnChanges = getGridCurrent("grid4").getChange();

      const headerInserts = qtiohChanges.insert ?? [];
      const lineInserts = qtioChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        qtiohChanges.modifiedRowcount === 0 &&
        qtioChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_io",
        cd_module: "MTL",
        no_line: 37,
        dt: focusedRowNodeH.data.dt_io,
        girdrefs: [
          gridRefs.current["grid1"],
          gridRefs.current["grid2"],
          gridRefs.current["grid4"],
        ],
        inserts: [qtiohChanges, qtioChanges, qtiolotsnChanges],
        cd_menu: Menuprops.cd_menu,
      });

      // 요청 데이터 구성
      const SaveData = {
        qtioh: qtiohChanges,
        qtio: qtioChanges,
        qtio_lotsn: qtiolotsnChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_MOW_REG_010_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid4").resetUpdate();
      setLotSnData([]);
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid4").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_io: today,
        cd_io: "top1",
        fg_trns: "1",
        rt_exch: 1,
        cd_exch: "000",
        tp_um: "001",
        tp_slip: "5",
        fg_slip: "2",
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        no_hst: 0,
        no_io: "",
        cd_wh: "",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io, "noEvent");
        rowNode.setDataValue("fg_money", ioType.fg_money, "noEvent");
        rowNode.setDataValue("fg_map", ioType.fg_map, "noEvent");

        if (rowCount > 0) {
          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            node.setDataValue(colId, rowData.cd_io, "noEvent");
            node.setDataValue("fg_io", ioType.fg_io, "noEvent");
            node.setDataValue("fg_money", ioType.fg_money, "noEvent");
            node.setDataValue("fg_map", ioType.fg_map, "noEvent");
          });
        }
        break;
      case "dt_io":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.dt_io);
        });
        getGridCurrent("grid4").forEachNode((node: RowNode) => {
          if (node.data.no_io === rowData.no_io) {
            node.setDataValue(colId, rowData.dt_io);
          }
        });
        break;
      case "cd_wh_ref":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.cd_wh_ref, "noEvent");
        });
        break;
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_io);

      const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

      if (rowCount > 0) {
        getGridCurrent("grid2").clearFocus();
        getGridCurrent("grid2").selectAndFocusRow(0);
      } else {
        getGridCurrent("grid4").applyFilters({
          no_io: "",
          no_line_io: 0,
          qt_inv: "notZero",
        });
      }
    } else {
      return;
    }
  };
  const onGridHBtnsClick = async (id: string) => {
    // 신규행이 없으면 Dialog로 띄우기
    const qtiohChanges = getGridCurrent("grid1").getChange().insert;
    if (qtiohChanges.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    let hasInvalid = false;
    getGridCurrent("grid2").forEachNode((node: RowNode) => {
      if (node.data.state === "insert") {
        hasInvalid = true;
      }
    });

    if (hasInvalid) {
      await showDialog("alter", "등록중인 건이 있습니다.");
      return;
    }

    switch (id) {
      case "btn_POP1":
        // searchPanel에서 선택된 값 전달
        const selectedFormData = searchRef.current.getFormdata();
        const selectedData = {
          cd_biz: selectedFormData.cd_biz,
          dt_from: selectedFormData.dt_from,
          dt_to: selectedFormData.dt_to,
        };

        // 신규행 포커스 잡아주기
        let focusedRowHIdx = 0;
        getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
          if (node.data.state === "insert") {
            focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
          }
        });

        getGridCurrent("grid1").selectAndFocusRow(focusedRowHIdx);

        const component = (
          <SN_MTL_MOW_REG_010_POP_001 selectedData={selectedData} />
        );
        const result = await showResizablePopupModal({
          title: "불출적용",
          children: component,
          validation: true,
        });

        if (!result || result === "close") return;

        const headerResult = result[0];
        const lineResult = result[1];

        // 요청도움창 Header 결과 적용
        const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
        if (focusedRowNodeH && headerResult[0]) {
          for (const key in headerResult[0]) {
            switch (key) {
              case "cd_wh":
              case "nm_wh":
                focusedRowNodeH.setDataValue(
                  key,
                  headerResult[0][key],
                  "noEvent"
                );
                break;
            }
          }
        }

        const qtLotItemList: any = [];
        const snItemList: any = [];

        // 요청도움창 Line 결과 적용
        lineResult.map((result: any) => {
          if (headerResult[0]) {
            const addedNode = onAddRow();
            if (addedNode) {
              for (const key in result) {
                switch (key) {
                  case "no_line":
                    addedNode.setDataValue(
                      "no_line_src",
                      result.no_line,
                      "noEvent"
                    );
                    break;
                  case "no_io":
                    addedNode.setDataValue("no_src", result.no_io, "noEvent");
                    break;
                  case "qt":
                    break;
                  default:
                    addedNode.setDataValue(key, result[key], "noEvent");
                    break;
                }
              }
              addedNode.setDataValue("yn_req", "Y", "noEvent");
              addedNode.setDataValue("cd_wh", headerResult[0].cd_wh, "noEvent");

              switch (result.fg_unit) {
                case "LOT":
                case "QT":
                  qtLotItemList.push({
                    cd_biz: focusedRowNodeH.data.cd_biz,
                    no_io: result.no_io,
                    no_line_io: result.no_line,
                  });
                  break;
                case "SN":
                  snItemList.push({
                    cd_biz: focusedRowNodeH.data.cd_biz,
                    no_io: result.no_io,
                    no_line_io: result.no_line,
                  });
                  break;
              }

              getGridCurrent("grid2").applyBlock(addedNode);
            }
          }
        });
        getGridCurrent("grid1").applyBlock(focusedRowNodeH);

        if (qtLotItemList.length > 0) {
          const searchLotData = await fnGetBfWhQtOrLotInv(qtLotItemList);
          const lotData = searchLotData.map((item: any) => {
            const { no_lot, ...rest } = item; // no_lot만 추출
            return {
              ...rest,
              no_barcode: no_lot, // 필드명 바꾸기
              no_src: item.no_io, // 원인전표
              no_line_src: item.no_line_io, // 원인전표항번
              qt_isu: 0, // 적용량 추가
              qt_waste: 0, // LOSS량 추가
            };
          });

          setLotSnData((prevData: any[]) => {
            // 기존에 [품목+창고]가 있는지 확인하여 없는 것만 필터링
            const newData = lotData.filter(
              (newItem: any) =>
                !prevData.some(
                  (oldItem: any) =>
                    oldItem.no_io === newItem.no_io &&
                    oldItem.no_line_io === newItem.no_line_io
                )
            );

            // 추가할 데이터가 있으면 합치고, 없으면 기존 상태 유지
            return newData.length === 0 ? prevData : [...prevData, ...newData];
          });
        }
        if (snItemList.length > 0) {
          const searchSnData = await fnGetBfWhSnInv(snItemList);
          const snData = searchSnData.map((item: any) => {
            const { no_sn, ...rest } = item;
            return {
              ...rest,
              no_barcode: no_sn, // 필드명 바꾸기
              no_src: item.no_io, // 원인전표
              no_line_src: item.no_line_io, // 원인전표항번
              qt_isu: 1, // 적용량 추가
              qt_waste: 0, // LOSS량 추가
              no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
              no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
            };
          });

          setLotSnData((prevData: any[]) => {
            // 기존에 [원천전표+원천항번]가 있는지 확인하여 없는 것만 필터링
            const newData = snData.filter(
              (newItem: any) =>
                !prevData.some(
                  (oldItem: any) =>
                    oldItem.no_io === newItem.no_io &&
                    oldItem.no_line_io === newItem.no_line_io
                )
            );

            // 추가할 데이터가 있으면 합치고, 없으면 기존 상태 유지
            return newData.length === 0 ? prevData : [...prevData, ...newData];
          });
        }
        break;
    }
  };
  //#endregion

  //#region onFocusedRowChangedL
  const onFocusedRowChangedL = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const insertRows = getGridCurrent("grid2").getChange().insert ?? [];
      const isInsertRow = insertRows.some(
        (row: any) =>
          row.no_line === selectedRow.no_line && row.no_io === selectedRow.no_io
      );
      if (isInsertRow) {
        getGridCurrent("grid3").applyFilters({
          no_io: selectedRow.no_src,
          no_line_io: selectedRow.no_line_src,
          qt_inv: "notZero",
        });
      } else {
        getGridCurrent("grid3").applyFilters({
          no_io: "",
          no_line_io: 0,
        });
      }
    } else {
      getGridCurrent("grid3").applyFilters({
        no_io: "",
        no_line_io: 0,
      });
    }
  };
  //#endregion

  //#region GridL Events
  const onBeforeAddRowL = (): any => {
    try {
      getGridCurrent("grid1").stopEditing();
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

      // 통합메뉴는 출고창고 못바꾸게 통제
      getGridCurrent("grid1").applyBlock(focusedRowNodeH);

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const updatedRow = {
        cd_firm: focusedRowNodeH.data?.cd_firm,
        cd_biz: focusedRowNodeH.data?.cd_biz,
        cd_dept: focusedRowNodeH.data?.cd_dept,
        cd_gp: focusedRowNodeH.data?.cd_gp,
        cd_emp: focusedRowNodeH.data?.cd_emp,
        dt_io: focusedRowNodeH.data?.dt_io,
        no_line: maxNoLine + 1,
        no_io: focusedRowNodeH.data?.no_io,
        cd_io: focusedRowNodeH.data?.cd_io,
        fg_slip: focusedRowNodeH.data?.fg_slip,
        tp_slip: focusedRowNodeH.data?.tp_slip,
        fg_io: focusedRowNodeH.data?.fg_io,
        fg_money: focusedRowNodeH.data?.fg_money,
        fg_vat: "11",
        rt_exch: focusedRowNodeH.data?.rt_exch,
        fg_map: focusedRowNodeH.data?.fg_map,
        qt: 0,
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        um: 0,
        um_k: 0,
        um_supply: 0,
        cd_loc: "",
        qt_good: 0,
        qt_bad: 0,
        yn_req: "N",
        cd_wh: focusedRowNodeH.data?.cd_wh,
        qt_waste: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "qt_good":
      case "qt_bad":
        rowNode.setDataValue(
          "qt",
          roundNum(rowData.qt_good + rowData.qt_bad),
          "noEvent"
        );
        break;
    }
  };

  const onGridLBtnsClick = async (id: string) => {
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };
  //#endregion

  //#region onCellValueChangedLotSn
  const onCellValueChangedLotSn = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "qt_isu":
      case "qt_waste":
        if (
          roundNum(rowData.qt_inv) < roundNum(rowData.qt_isu + rowData.qt_waste)
        ) {
          showDialog("alter", "잔량을 초과했습니다.");
          rowNode.setDataValue("qt_isu", 0, "noEvent");
          rowNode.setDataValue("qt_waste", 0, "noEvent");
        }
        break;
    }
  };

  const onRowDataUpdatedLotSn = () => {
    // 직접바코드 스캔이면 grid2 포커스 상태와 무관하게 바코드칸으로 복귀한다.
    // (기존 insert행에 수량만 합산되는 경우엔 grid2 포커스가 insert가 아니라 조건이 어긋나
    //  복귀가 스킵됐다. isDirectScan 이 이미 "방금 직접 스캔함"을 의미하므로 조건 없이 복귀.)
    if (isDirectScan.current) {
      // 첫행 추가될때만 안돼서 rAF 2번으로 렌더 정착 후 포커스
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          txtDirectBarcodeRef.current?.focus();
          isDirectScan.current = false;
        });
      });
      return;
    }

    // 자재리스트 스캔(토글) 경로 — 신규(insert)이거나 포커스 없을 때만 바코드칸으로.
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
    if (!focusedRowNodeL || focusedRowNodeL.data.state === "insert") {
      txtBarcodeRef.current.focus();
    }
  };

  const onModelUpdatedLotSn = () => {
    // 적용팝업 푸터갱신
    getGridCurrent("grid3").refreshFooter();
  };
  //#endregion

  const onGridDBtnsClick = async (id: string) => {
    switch (id) {
      case "addRow":
        selectInv("C", "");
        break;
      case "deleteRow":
        if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
          return;
        }

        const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();
        const focusedNodeInfo = getGridCurrent("grid4").focusedRowNode();

        getGridCurrent("grid3").forEachNode((node: RowNode) => {
          if (
            node.data.cd_matl === focusedNodeInfo.data?.cd_item &&
            node.data.cd_wh === focusedNodeInfo.data?.cd_wh &&
            node.data.no_barcode === focusedNodeInfo.data?.no_barcode
          ) {
            node.setDataValue(
              "qt_inv",
              roundNum(
                node.data.qt_inv +
                  focusedNodeInfo.data?.qt_good +
                  focusedNodeInfo.data?.qt_waste
              )
            );

            if (node.data.fg_unit === "SN") {
              node.setDataValue("qt_isu", 1);
            }

            focusedRowNodeL.setDataValue(
              "qt_good",
              roundNum(
                focusedRowNodeL.data?.qt_good - focusedNodeInfo.data?.qt_good
              )
            );
            return;
          }
        });
        getGridCurrent("grid4").deleteSelectedRows();
        getGridCurrent("grid3").applyFilters({
          no_io: focusedRowNodeL.data.no_src,
          no_line_io: focusedRowNodeL.data.no_line_src,
          qt_inv: "notZero",
        });
        break;
    }
  };
  //#endregion

  //#region onDirectBarcodeEnter
  const onDirectBarcodeEnter = async (e: any) => {
    const barcode = e;

    if (converter.getString(barcode) === "") {
      return;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    if (!focusedRowNodeH) {
      await showDialog("alter", "선택된 행이 없습니다.");
      return;
    } else if (focusedRowNodeH.data.cd_wh === "") {
      await showDialog("alter", "출고창고가 선택되지 않았습니다.");
      return;
    }

    isDirectScan.current = true;

    const cdWh = focusedRowNodeH.data.cd_wh;

    const params = {
      cd_biz: focusedRowNodeH.data.cd_biz,
      cd_wh: cdWh,
      no_barcode: barcode,
    };

    const scanedList: any[] = [];

    getGridCurrent("grid3").forEachNode((node: RowNode) => {
      scanedList.push(node.data.no_barcode);
    });

    const searchBarcodeData = await fnGetBarcodeScanBfInv(params);

    if (!searchBarcodeData) return;

    if (searchBarcodeData.length < 1) {
      await showDialog("alter", "재고 정보가 없거나 잘못된 바코드입니다.");
      txtDirectBarcodeRef.current.clear();
      return;
    }

    const scannedBarcodes = searchBarcodeData.map((item: any) => {
      return item.no_lot !== "" ? item.no_lot : item.no_sn;
    });

    let hasInvalid = false;
    getGridCurrent("grid3").forEachNode((node: RowNode) => {
      if (scannedBarcodes.includes(node.data.no_barcode)) {
        hasInvalid = true;
      }
    });

    if (hasInvalid) {
      await showDialog("alter", "이미 스캔한 바코드 정보입니다.");
      txtDirectBarcodeRef.current.clear();
      return;
    }

    let qtInv = numberDirectBarcodeQtRef.current.getValue;

    // SN재고가아니면서 수량이 안 찍혔으면 멈추고 수량 값 있거나 SN자재면 진행
    if (searchBarcodeData[0]["fg_unit"] !== "SN") {
      if (qtInv === 0) {
        numberDirectBarcodeQtRef.current.focus();
        return;
      }
    } else {
      qtInv = 1;
    }

    // 동일 바코드의 재고전표가 여러 줄일 수 있으므로 재고량 합계로 비교
    const qtBarcodeInv = searchBarcodeData.reduce(
      (sum: number, item: any) => roundNum(sum + (item.qt_inv ?? 0)),
      0
    );
    if (qtInv > qtBarcodeInv) {
      await showDialog(
        "alter",
        `재고량 보다 반납량이 큽니다.\r\n해당 바코드 현재고량: [${qtBarcodeInv}]`
      );
      numberDirectBarcodeQtRef.current.setValue(0);
      numberDirectBarcodeQtRef.current.focus();
      return;
    }

    // 초기화
    txtDirectBarcodeRef.current.clear();
    numberDirectBarcodeQtRef.current.setValue(0);

    const invItemList: any = [];

    // 데이터가 어떻게 돼있을지 모르므로 0번행 품목기준으로만 보게
    // 재고전표가 여러 줄이면 입력수량을 앞 전표부터 순서대로 분배 (예: 1000/500 재고에 1200 입력 → 1000, 200)
    let qtRemain = qtInv;
    searchBarcodeData.forEach((item: any) => {
      const qtAlloc =
        item.fg_unit === "SN" ? 1 : Math.min(qtRemain, item.qt_inv ?? 0);
      if (qtAlloc <= 0) return;
      if (item.fg_unit !== "SN") qtRemain -= qtAlloc;

      let addedItem = false;
      let itemNodeData: any;
      let itemNode: any;

      // 같은 원인번호 품목 신규등록 중인 행이 있는지 찾기
      getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
        if (addedItem) return;

        if (
          node.data.cd_item === item.cd_item &&
          node.data.no_src === item.no_io &&
          node.data.no_line_src === item.no_line_io &&
          node.data.state === "insert"
        ) {
          addedItem = true;
          itemNodeData = node.data;
          itemNode = node;
        }
      });

      // 없는 품목이면 자동추가
      if (!addedItem) {
        const addedNode = getGridCurrent("grid2").addRow();
        addedNode?.setDataValue("cd_item", item.cd_item, "noEvent");
        addedNode?.setDataValue("nm_item", item.nm_item, "noEvent");
        addedNode?.setDataValue("dc_item_spec", item.dc_item_spec, "noEvent");
        addedNode?.setDataValue("dc_item_unit", item.dc_item_unit, "noEvent");
        addedNode?.setDataValue("fg_unit", item.fg_unit, "noEvent");
        addedNode?.setDataValue("qt_good", qtAlloc, "noEvent");
        addedNode?.setDataValue("qt", qtAlloc, "noEvent");

        addedNode?.setDataValue("no_src", item.no_io, "noEvent");
        addedNode?.setDataValue("no_line_src", item.no_line_io, "noEvent");
        itemNodeData = addedNode?.data;
      }
      // 있으면 수량 추가
      else {
        itemNode.setDataValue(
          "qt_good",
          itemNodeData.qt_good + qtAlloc,
          "noEvent"
        );
        itemNode.setDataValue("qt", itemNodeData.qt + qtAlloc, "noEvent");
      }

      switch (item.fg_unit) {
        case "LOT":
          {
            const newItem = {
              no_barcode: item.no_lot,
              qt_inv: roundNum(item.qt_inv - qtAlloc),
              qt_isu: 0, // 적용량
              qt_waste: 0, // LOSS량
              no_src: "", // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
              no_line_src: -1, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
              cd_matl: item.cd_item,
              cd_wh: item.cd_wh,
              fg_unit: item.fg_unit,
              no_io: item.no_io,
              no_line_io: item.no_line_io,
              no_origin: "", // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
              no_line_origin: -1, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
            };
            invItemList.push(newItem);
          }
          break;
        case "SN":
          {
            const newItem = {
              no_barcode: item.no_sn,
              qt_inv: 0,
              qt_isu: 1, // 적용량
              qt_waste: 0, // LOSS량
              no_src: item.no_line, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
              no_line_src: item.no_line_io, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
              cd_matl: item.cd_item,
              cd_wh: item.cd_wh,
              fg_unit: item.fg_unit,
              no_io: item.no_io,
              no_line_io: item.no_line_io,
              no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
              no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
            };
            invItemList.push(newItem);
          }
          break;
      }

      const newRow = {
        cd_firm: itemNodeData.cd_firm,
        cd_biz: itemNodeData.cd_biz,
        no_io: itemNodeData.no_io,
        no_line: itemNodeData.no_line,
        dt_io: itemNodeData.dt_io,
        tp_slip: itemNodeData.tp_slip,
        fg_slip: itemNodeData.fg_slip,
        fg_io: itemNodeData.fg_io,
        fg_money: itemNodeData.fg_money,
        cd_io: itemNodeData.cd_io,
        cd_item: itemNodeData.cd_item,
        cd_wh: itemNodeData.cd_wh,
        no_barcode: item.no_lot !== "" ? item.no_lot : item.no_sn,
        fg_unit: item.fg_unit,
        qt_good: qtAlloc,
        qt_waste: 0,
        no_src: item.no_io, // LOT/SN 수불 원인전표
        no_line_src: item.no_line_io, // LOT/SN 수불 원인전표
        no_origin: item.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        no_line_origin: item.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
      };

      getGridCurrent("grid4").addRow(newRow);
    });

    const prevInvData: any = [];
    getGridCurrent("grid3").forEachNode((node: RowNode) => {
      prevInvData.push(node.data);
    });
    setLotSnData([...prevInvData, ...invItemList]);
  };

  const onDirectBarcodeQtEnter = async (e: any) => {
    await onDirectBarcodeEnter(txtDirectBarcodeRef.current.getValue());
  };
  //#endregion

  //#region onBarcodeEnter
  const onBarcodeEnter = async (e: any) => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    getGridCurrent("grid3").applyFilters({
      no_io: focusedRowNodeL.data.no_src,
      no_line_io: focusedRowNodeL.data.no_line_src,
      qt_inv: "notZero",
    });

    const barcode = e;

    if (converter.getString(barcode) === "") {
      return;
    }

    // 스캔로직
    if (!switchRef.current?.getChecked()) {
      const qtScan = numberBarcodeQtRef.current.getValue;

      if (qtScan === 0) {
        // applyFilters때문에
        requestAnimationFrame(() => {
          numberBarcodeQtRef.current.focus();
        });
        return;
      }
      txtBarcodeRef.current.clear();
      selectInv("S", barcode, qtScan);
    } else {
      getGridCurrent("grid3").applyLikeFilters(
        {
          cd_matl: focusedRowNodeL.data.cd_item,
          cd_wh: focusedRowNodeH.data.cd_wh,
          qt_inv: "notZero",
          no_barcode: barcode,
        },
        ["no_barcode"]
      );
    }
  };

  const onBarcodeQtEnter = async (e: any) => {
    await onBarcodeEnter(txtBarcodeRef.current.getValue());
  };
  //#endregion

  //#region Method
  const selectInv = (id: string, barcode: string, qtScan?: number) => {
    const focusedRowNodeL = getGridCurrent("grid2").focusedRowNode();

    if (focusedRowNodeL == null) {
      showDialog("alter", "선택된 행이 없습니다.");
      return false;
    }

    if (focusedRowNodeL.data.state !== "insert") {
      showDialog("alter", "신규 행에서만 수정 가능합니다.");
      return false;
    }

    let selectedMap = new Map<string, { qtIsu: number; qtWaste: number }>();
    let qtAdd = 0;

    switch (id) {
      case "C": // 재고선택
        getGridCurrent("grid3").stopEditing();
        const selectedLotSn = getGridCurrent("grid3").getCheckedRows();
        if (selectedLotSn.length < 1) {
          showDialog("alter", "선택된 재고가 없습니다.");
          return false;
        }

        selectedLotSn.forEach((row) => {
          const key = row.no_barcode;
          selectedMap.set(key, {
            qtIsu: Number(row.qt_isu) || 0,
            qtWaste: Number(row.qt_waste) || 0,
          });
        });
        break;
    }

    const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

    getGridCurrent("grid3").forEachFilteredNode((node: RowNode) => {
      const row = node.data;
      let qtIsu = 0;
      let qtWaste = 0;

      switch (id) {
        case "C": // 선택
          const key = row.no_barcode;
          const selectedData = selectedMap.get(key);

          if (selectedData) {
            qtIsu = selectedData.qtIsu; // 반납량 가져옴
            qtWaste = selectedData.qtWaste; // LOSS량 가져옴
          }
          break;
        case "S": // 스캔
          if (
            String(row.no_barcode ?? "").toLowerCase() === barcode.toLowerCase()
          ) {
            qtIsu = Math.min(qtScan ?? 0, Number(row.qt_inv) || 0);
          }
          break;
      }

      if (qtIsu === 0 && qtWaste === 0) return;

      const newRow = {
        cd_firm: focusedRowNodeL.data?.cd_firm,
        cd_biz: focusedRowNodeL.data?.cd_biz,
        no_io: focusedRowNodeL.data?.no_io,
        no_line: focusedRowNodeL.data?.no_line,
        dt_io: focusedRowNodeL.data?.dt_io,
        tp_slip: focusedRowNodeL.data?.tp_slip,
        fg_slip: focusedRowNodeL.data?.fg_slip,
        fg_io: focusedRowNodeL.data?.fg_io,
        fg_money: focusedRowNodeL.data?.fg_money,
        cd_io: focusedRowNodeL.data?.cd_io,
        cd_item: focusedRowNodeL.data?.cd_item,
        cd_wh: focusedRowNodeH.data?.cd_wh,
        no_barcode: row.no_barcode,
        fg_unit: row.fg_unit,
        qt_good: qtIsu,
        qt_waste: qtWaste,
        no_src: row.no_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        no_line_src: row.no_line_src, // SN재고일때 원복을 위해 필요한 값 | LOT재고는 무의미
        no_origin: row.no_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        no_line_origin: row.no_line_origin, // 시리얼 재고일 경우 원 입고전표,항번 넣어줌 | LOT재고는 무의미
        no_batch: node.data.no_batch, // 26.11.02 추가
      };

      qtAdd += qtIsu; // 총 출고량 더하고

      node.setDataValue("qt_isu", 0, "noEvent");
      node.setDataValue("qt_waste", 0, "noEvent");
      node.setDataValue(
        "qt_inv",
        roundNum(node.data.qt_inv - qtIsu - qtWaste),
        "noEvent"
      );

      let isExist = false;
      // 기존항목에 있으면 수량만 업데이트
      getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
        if (
          node4.data.cd_item === focusedRowNodeL.data?.cd_item &&
          node4.data.cd_wh === focusedRowNodeH.data?.cd_wh &&
          node4.data.no_barcode === node.data.no_barcode
        ) {
          node4.setDataValue("qt_good", roundNum(node4.data.qt_good + qtIsu));
          node4.setDataValue(
            "qt_waste",
            roundNum(node4.data.qt_waste + qtWaste)
          );
          isExist = true;
          return;
        }
      });
      if (!isExist) {
        getGridCurrent("grid4").addRow(newRow);
      }
    });

    focusedRowNodeL.setDataValue(
      "qt_good",
      focusedRowNodeL.data?.qt_good + qtAdd
    );

    getGridCurrent("grid3").clearCheckBox();
    getGridCurrent("grid3").applyFilters({
      no_io: focusedRowNodeL.data.no_src,
      no_line_io: focusedRowNodeL.data.no_line_src,
      qt_inv: "notZero",
    });

    if (id === "S") {
      if (qtAdd === 0) {
        showDialog("alter", "바코드 정보를 찾을 수 없습니다.");
      }
    }
  };

  const resetRowData = (): any => {
    if (getGridCurrent("grid4").getDisplayedRowCount() < 1) {
      return;
    }

    getGridCurrent("grid4").forEachFilteredNode((node4: RowNode) => {
      getGridCurrent("grid3").forEachNode((node: RowNode) => {
        if (
          node4.data.cd_item === node.data?.cd_matl &&
          node4.data.cd_wh === node.data?.cd_wh &&
          node4.data.no_barcode === node.data?.no_barcode
        ) {
          node.setDataValue(
            "qt_inv",
            roundNum(
              node.data.qt_inv + node4.data.qt_good + node4.data.qt_waste
            )
          );

          if (node.data?.fg_unit === "SN") {
            node.setDataValue("qt_isu", 1);
          }
          return;
        }
      });
    });
    getGridCurrent("grid4").deleteAllRows();

    getGridCurrent("grid3").applyFilters({
      no_io: "",
      no_line_io: 0,
    });
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onCellValueChanged: onCellValueChangedH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onFocusedRowChanged: onFocusedRowChangedL,
          onCellValueChanged: onCellValueChangedL,
          onBtnDeleteClick: onGridLBtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: lotSnData,
        events: {
          onCellValueChanged: onCellValueChangedLotSn,
          onRowDataUpdated: onRowDataUpdatedLotSn,
          onModelUpdated: onModelUpdatedLotSn,
        },
      },
      grid4: {
        columns: columns.grid4,
        dataSource: dataSources.grid4,
        events: {
          onBtnAddClick: onGridDBtnsClick,
          onBtnDeleteClick: onGridDBtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="35.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "반납정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_io"] }}
            header={
              <div>
                <SnButton
                  text={"불출적용"}
                  onClick={() => {
                    onGridHBtnsClick("btn_POP1");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="55.5%">
          <div className="flex w-full h-full min-w-0 space-x-1.5">
            <SnResizeBar direction={"width"} id="panel3" orgSize="60%">
              <SnGrid
                ref={gridRefs.current["grid2"]} // Grid Name
                columnConfig={grids.grid2.columns} // 컬럼 설정 전달
                rowData={grids.grid2.dataSource} // Datasource
                etcProps={{ rowSelection: "multiple", title: "상세정보" }}
                events={grids.grid2.events}
                filter={{
                  refs: [gridRefs.current["grid4"]],
                  columns: ["no_io", "no_line"],
                }}
                header={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div style={{ width: 40 }}>
                      <SnLabel text="바코드" type="string" />
                    </div>
                    <SnBarcodeBox
                      onEnter={onDirectBarcodeEnter}
                      ref={txtDirectBarcodeRef}
                      inputRef={txtDirectBarcodeInputRef}
                      isAutoClear={false}
                    />
                    <div style={{ width: "120px" }}>
                      <SnNumberBox
                        onEnter={onDirectBarcodeQtEnter}
                        inputRef={numberDirectBarcodeQtRef}
                        isAutoClear={true}
                      />
                    </div>
                  </div>
                }
              />
            </SnResizeBar>
            <div className="flex-1 min-w-0 flex flex-col h-full space-y-1.5">
              <SnResizeBar direction={"height"} id="panel4" orgSize="50%">
                <SnGrid
                  ref={gridRefs.current["grid3"]} // Grid Name
                  columnConfig={grids.grid3.columns} // 컬럼 설정 전달
                  rowData={lotSnData} // Datasource
                  etcProps={{
                    rowSelection: "single",
                    title: "자재리스트",
                    ischeckboxSelection: true,
                  }}
                  events={grids.grid3.events}
                  header={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <SnSwitch
                        ref={switchRef}
                        labelCheck="검색"
                        labelUncheck="스캔"
                        defaultChecked={false}
                      />
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <SnBarcodeBox
                          onEnter={onBarcodeEnter}
                          ref={txtBarcodeRef}
                          inputRef={txtBarcodeInputRef}
                          isAutoClear={false}
                        />
                        <div style={{ width: "120px" }}>
                          <SnNumberBox
                            onEnter={onBarcodeQtEnter}
                            inputRef={numberBarcodeQtRef}
                            isAutoClear={true}
                          />
                        </div>
                      </div>
                    </div>
                  }
                />
              </SnResizeBar>
              <div className="flex-1 min-h-0">
                <SnGrid
                  ref={gridRefs.current["grid4"]} // Grid Name
                  columnConfig={grids.grid4.columns} // 컬럼 설정 전달
                  rowData={grids.grid4.dataSource} // Datasource
                  etcProps={{ rowSelection: "single", title: "반납자재정보" }}
                  events={grids.grid4.events}
                />
              </div>
            </div>
          </div>
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_MOW_REG_010;
